export type InAppBrowserName =
  | "facebook"
  | "instagram"
  | "line"
  | "twitter"
  | "snapchat"
  | "tiktok";

export function detectInAppBrowserFromUa(ua: string | undefined | null): InAppBrowserName | null {
  if (!ua) return null;
  if (/FBAN|FBAV/i.test(ua)) return "facebook";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/Line\//i.test(ua)) return "line";
  if (/Twitter|X-Twitter/i.test(ua)) return "twitter";
  if (/Snapchat/i.test(ua)) return "snapchat";
  if (/TikTok/i.test(ua)) return "tiktok";
  return null;
}

/**
 * Detects in-app browser environments (Instagram, Facebook, etc.)
 * where cross-origin cookie restrictions often prevent third-party
 * tracking SDKs from functioning correctly.
 *
 * Returns the platform name or null for standard browsers.
 */
export function detectInAppBrowser(): InAppBrowserName | null {
  if (typeof navigator === "undefined") return null;
  return detectInAppBrowserFromUa(navigator.userAgent);
}

/** Safe accessor for navigator.userAgent that works in SSR contexts. */
export function getUserAgent(): string {
  if (typeof navigator === "undefined") return "unknown";
  return navigator.userAgent;
}

/**
 * Extracts attribution-relevant version strings from a user agent.
 * Used to correlate Everflow tracking failures with specific app / WebView
 * versions so we can distinguish "known-bad FB app 523.x" from regressions
 * in newer builds.
 */
export function parseUaVersions(ua: string | undefined | null): {
  fbAppVersion?: string;
  webviewChromeVersion?: string;
  iosWebKitVersion?: string;
} {
  if (!ua) return {};
  const fb = ua.match(/FBAV\/([\d.]+)/);
  const chrome = ua.match(/Chrome\/([\d.]+)/);
  const webkit = ua.match(/Version\/([\d.]+)/);
  return {
    ...(fb && { fbAppVersion: fb[1] }),
    ...(chrome && { webviewChromeVersion: chrome[1] }),
    ...(webkit && { iosWebKitVersion: webkit[1] }),
  };
}
